import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderPanel
} from 'carbon-components-react/lib/components/UIShell';
import { Loading } from 'carbon-components-react';
import { setHeaderPanelExpand } from 'redux/app/actions';
import { selectHeaderPanelExpand } from 'redux/app/selectors';
import { SCREEN_SIZE, TEAM_NAME } from 'types';
import {
  selectUserProcess,
  selectScreenSize,
  currentUser,
  selectorAlertNotificationShowHide
} from 'redux/session/selectors';
import {
  selectTeamPickerDisabled,
  selectCurrentTeamName
} from 'redux/team/selectors';
import {
  Close16,
  Events16,
  Notification16,
  UserAvatar16,
  AppSwitcher16
} from '@carbon/icons-react';
import { useEventListener } from 'utilities/hooks.util';
import {
  TeamsPicker,
  Notification,
  UserMenu,
  TeamsTeaser
} from '../HeaderPanel';
import { FeedbackPanel } from 'modules';

type PanelType = 'teams' | 'notification' | 'menu' | 'feedback';

const AuthGlobalToolBar: React.FC<Props> = ({ disableNav }) => {
  const dispatch = useDispatch();
  const headerPanelExpand: any = useSelector(selectHeaderPanelExpand);
  const screenSize = useSelector(selectScreenSize) || SCREEN_SIZE.MAX;
  const teamName = useSelector(selectCurrentTeamName);
  const teampickerDisabled = useSelector(selectTeamPickerDisabled);
  const user = useSelector(currentUser);
  const userProcess = useSelector(selectUserProcess);
  const alertNotificationToggle = useSelector(
    selectorAlertNotificationShowHide
  );
  const [currentPanel, setCurrentPanel] = useState<PanelType>();
  const containerRef = useRef<HTMLDivElement>(null);

  const closePanel = () => {
    setCurrentPanel(undefined);
    dispatch(setHeaderPanelExpand({ isOpen: false }));
  };

  const openPanel = (type: PanelType) => {
    setCurrentPanel(type);
    dispatch(setHeaderPanelExpand({ panel: type, isOpen: true }));
  };

  const renderPanel = (type: PanelType | undefined, user: any) => {
    switch (type) {
      case 'teams':
        return user?.is_trial ? (
          <TeamsTeaser onOpenpanel={openPanel} />
        ) : (
          <TeamsPicker onClose={closePanel} onOpenpanel={openPanel} />
        );
      case 'notification':
        return (
          <Notification onOpenpanel={openPanel} isTrialUser={user?.is_trial} />
        );
      case 'menu':
        return <UserMenu disableNav={disableNav} onOpenpanel={openPanel} />;
      case 'feedback':
        return <FeedbackPanel onClose={closePanel} onOpenpanel={openPanel} />;
      default:
        return <></>;
    }
  };

  const handleClickOutside = (evt: any) => {
    const headerGlobalDom = document
      .getElementsByClassName('bx--header__global')
      ?.item(0);
    if (
      containerRef.current &&
      evt.target &&
      headerPanelExpand?.isOpen &&
      headerGlobalDom
    ) {
      if (
        !containerRef.current.contains(evt.target) &&
        !headerGlobalDom.contains(evt.target)
      ) {
        closePanel();
      }
    }
  };

  useEffect(() => {
    if (headerPanelExpand) {
      const { isOpen, panel } = headerPanelExpand;
      if (isOpen) {
        setCurrentPanel(panel);
      }
    }
  }, [dispatch, headerPanelExpand]);

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  return (
    <>
      <HeaderGlobalBar>
        {screenSize !== SCREEN_SIZE.SM ? (
          <>
            <div
              className={`team-name${
                teampickerDisabled ||
                (headerPanelExpand?.isOpen === true &&
                  headerPanelExpand?.panel !== 'teams')
                  ? ' disabled'
                  : ''
              }`}
              onClick={
                teampickerDisabled ||
                (headerPanelExpand?.isOpen === true &&
                  headerPanelExpand?.panel !== 'teams')
                  ? undefined
                  : () =>
                      headerPanelExpand?.panel === 'teams'
                        ? closePanel()
                        : openPanel('teams')
              }
            >
              {userProcess === 'loading' || userProcess === 'updating' ? (
                <Loading withOverlay={false} small />
              ) : (
                teamName || TEAM_NAME.PRIVATE
              )}
            </div>
            <HeaderGlobalAction
              aria-label="Teams"
              disabled={
                teampickerDisabled ||
                (headerPanelExpand?.isOpen === true &&
                  headerPanelExpand?.panel !== 'teams')
              }
              isActive={headerPanelExpand?.panel === 'teams'}
              onClick={() =>
                headerPanelExpand?.panel === 'teams'
                  ? closePanel()
                  : openPanel('teams')
              }
            >
              {headerPanelExpand?.panel === 'teams' ? (
                <Close16 />
              ) : (
                <Events16 />
              )}
            </HeaderGlobalAction>
            <HeaderGlobalAction
              aria-label="Notifications"
              disabled={
                headerPanelExpand?.isOpen === true &&
                headerPanelExpand?.panel !== 'notification'
              }
              isActive={headerPanelExpand?.panel === 'notification'}
              onClick={() =>
                headerPanelExpand?.panel === 'notification'
                  ? closePanel()
                  : openPanel('notification')
              }
            >
              {headerPanelExpand?.panel === 'notification' ? (
                <Close16 />
              ) : (
                <Notification16 />
              )}
            </HeaderGlobalAction>
            <HeaderGlobalAction
              aria-label="User"
              disabled={
                headerPanelExpand?.isOpen === true &&
                headerPanelExpand?.panel !== 'menu' &&
                headerPanelExpand?.panel !== 'feedback'
              }
              isActive={
                headerPanelExpand?.panel === 'menu' ||
                headerPanelExpand?.panel === 'feedback'
              }
              onClick={() =>
                headerPanelExpand?.panel === 'menu' ||
                headerPanelExpand?.panel === 'feedback'
                  ? closePanel()
                  : openPanel('menu')
              }
              tooltipAlignment="end"
            >
              {headerPanelExpand?.panel === 'menu' ||
              headerPanelExpand?.panel === 'feedback' ? (
                <Close16 />
              ) : (
                <UserAvatar16 />
              )}
            </HeaderGlobalAction>
          </>
        ) : (
          <HeaderGlobalAction
            aria-label="Menu"
            isActive={headerPanelExpand?.isOpen === true}
            onClick={() =>
              headerPanelExpand?.isOpen === true
                ? closePanel()
                : openPanel('menu')
            }
            tooltipAlignment="end"
          >
            {headerPanelExpand?.isOpen === true ? (
              <Close16 />
            ) : (
              <AppSwitcher16 />
            )}
          </HeaderGlobalAction>
        )}
      </HeaderGlobalBar>
      <HeaderPanel
        ref={containerRef}
        id="header-panel"
        className={`${
          alertNotificationToggle ? 'noti-alert-notification-header-panel' : ''
        } header-panel`}
        aria-label="Header Panel"
        expanded={headerPanelExpand?.isOpen === true}
      >
        <div className="panel-body">{renderPanel(currentPanel, user)}</div>
      </HeaderPanel>
    </>
  );
};

type Props = {
  disableNav?: boolean;
};

export default React.memo(AuthGlobalToolBar);
